import { PoolInstance, isInstance } from '@synergy/config';
import { ReactElement, createContext, useContext, useEffect } from 'react';
import { noop } from '@synergy/utils';
import { useIsMobile } from '@synergy/components-next';
import { useSearchParams } from 'react-router-dom';

import { SingleInquiryDrawer } from './single-inquiry-drawer';
import { SingleInquiryFormValues } from './models/single-inquiry-form-values';
import { SingleInquiryModal } from './single-inquiry-modal';
import { closeInquiryModal, sendSingleInquiry } from '../../../store/single-inquiry-actions';
import { singleInquiryDataSelector } from '../../../store/single-inquiry-selectors';
import { useAppDispatch, useAppSelector } from '../../../../store/store';

interface SingleInquiryContextProps {
    onClose: () => void;
    onSubmit: (values: SingleInquiryFormValues) => void;
}

const SingleInquiryContext = createContext<SingleInquiryContextProps>({
    onClose: noop,
    onSubmit: noop,
});

export const useSingleInquiryContext = (): SingleInquiryContextProps => useContext(SingleInquiryContext);

export const SingleInquiry = (): ReactElement | null => {
    const { isOpen } = useAppSelector(singleInquiryDataSelector);
    const isMobile = useIsMobile();
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();

    // Url query param sync
    useEffect(() => {
        if (isInstance(PoolInstance.AT)) {
            return;
        }

        if (isOpen) {
            searchParams.set('single-inquiry', 'true');
            setSearchParams(searchParams);
        } else {
            searchParams.delete('single-inquiry');
            setSearchParams(searchParams);
        }
    }, [isOpen, searchParams, setSearchParams]);

    const handleClose = (): void => {
        dispatch(closeInquiryModal());
    };

    const handleSubmit = (values: SingleInquiryFormValues): void => {
        dispatch(sendSingleInquiry(values));
        dispatch(closeInquiryModal());
    };

    return (
        <SingleInquiryContext.Provider
            value={{
                onClose: handleClose,
                onSubmit: handleSubmit,
            }}
        >
            {isMobile ? <SingleInquiryDrawer /> : <SingleInquiryModal />}
        </SingleInquiryContext.Provider>
    );
};
